<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { DocumentModal } from '@/entities/Document';
  import { DocumentControlSection, DocumentControlValiditySection } from '@/features/Document';
  import type { DocumentRevision, DocumentControlResponse } from '@/entities/Document';
  import { getDocumentControl } from '@/entities/Document';
  import { formatCivilDateToPtBR, formatInstantToPtBR } from '@/shared/lib';

  const props = defineProps<{
    title: string;
    revision: DocumentRevision | null;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const controlData = ref<DocumentControlResponse | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const formatPositions = (positions: { name: string }[]): string => {
    return positions.map((p) => p.name).join(', ');
  };

  const controlSections = computed(() => {
    if (!controlData.value) return [];

    const sections = [
      {
        title: 'Elaboração',
        responsible: controlData.value.elaboration.elaborator.name,
        cargo: formatPositions(controlData.value.elaboration.elaborator.positions),
        date: formatInstantToPtBR(controlData.value.elaboration.elaboratedAt),
      },
    ];

    if (controlData.value.approval) {
      sections.push({
        title: 'Aprovação',
        responsible: controlData.value.approval.approver.name,
        cargo: formatPositions(controlData.value.approval.approver.positions),
        date: formatInstantToPtBR(controlData.value.approval.approvedAt),
      });
    }

    if (controlData.value.verification) {
      sections.push({
        title: 'Verificação',
        responsible: controlData.value.verification.verifier.name,
        cargo: formatPositions(controlData.value.verification.verifier.positions),
        date: formatInstantToPtBR(controlData.value.verification.verifiedAt),
      });
    }

    return sections;
  });

  const vigenciaSectionInfo = computed(() => {
    const formattedDate = formatCivilDateToPtBR(controlData.value?.effectiveDate);

    if (formattedDate === '-') return null;

    return {
      date: formattedDate,
    };
  });

  const fetchControlData = async () => {
    if (!props.revision?.id) return;

    loading.value = true;
    error.value = null;

    try {
      controlData.value = await getDocumentControl(props.revision.id);
    } catch (err) {
      error.value = 'Erro ao carregar dados de controle do documento';
      console.error('Error fetching control data:', err);
    } finally {
      loading.value = false;
    }
  };

  onMounted(() => {
    fetchControlData();
  });
</script>

<template>
  <DocumentModal
    :title="title"
    :revision="revision"
    modal-title="Controle de Elaboração, Aprovação, Verificação e Homologação de Documentos do SGQ"
    document-info-title="CONTROLE DE ELABORAÇÃO, APROVAÇÃO, VERIFICAÇÃO E HOMOLOGAÇÃO DE DOCUMENTOS (SGQ)"
    document-number="RQ05.1"
    document-revision-label="REV.08"
    print-pill-label="Documento"
    :print-compact="true"
    @close="emit('close')"
  >
    <template #default>
      <div v-if="loading" class="flex justify-center items-center py-8">
        <div class="text-gray-600">Carregando dados de controle...</div>
      </div>

      <div v-else-if="error" class="flex justify-center items-center py-8">
        <div class="text-red-600">
          {{ error }}
        </div>
        <button
          class="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          @click="fetchControlData"
        >
          Tentar novamente
        </button>
      </div>

      <div v-else>
        <DocumentControlSection
          v-for="section in controlSections"
          :key="section.title"
          :title="section.title"
          :section-info="section"
        />

        <DocumentControlValiditySection :date="vigenciaSectionInfo?.date" />
      </div>
    </template>
  </DocumentModal>
</template>

<style scoped>
  @media print {
    .modal-header {
      display: none;
    }

    .print-button {
      display: none;
    }
  }
</style>
